import React, { useState, useMemo } from 'react';
import { usePopulationData } from '@/hooks/usePopulationData';
import { usePeerPopulationData } from '@/hooks/usePeerPopulationData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shared/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/shared/tooltip";
import { formatPlaceName, cn } from '@/lib/utils';
import { ComparisonBarChart } from './ComparisonBarChart';
import { Place } from '@/types';

interface Props {
  place: string | null;
  placeFips: string | null;
  peers: Place[];
  labelFontSize?: number;
}

const PopulationCard: React.FC<Props> = ({ place, placeFips, peers, labelFontSize }) => {
  const { data: placeData, loading, error } = usePopulationData(place, placeFips);
  const { data: peerData, loading: peersLoading } = usePeerPopulationData(peers);
  const [isTitleHovered, setIsTitleHovered] = useState(false);

  const getRankSuffix = (rank: number) => {
    if (rank > 10 && rank < 20) return 'th';
    const lastDigit = rank % 10;
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const chartData = React.useMemo(() => {
    if (!placeData.length || !peerData) return [];
    
    const mostRecentPlaceData = placeData[placeData.length - 1];

    const allCities = peers
      .filter(peer => peer.place_fips.toString() !== placeFips)
      .map(peer => {
        const peerRecords = peerData[peer.place_fips];
        const latestPeerRecord = peerRecords ? peerRecords[0] : null;
        return {
          name: peer.name,
          value: parseFloat((latestPeerRecord?.population_growth ?? 0).toFixed(2)),
          isSelected: false,
        };
      });

    if (mostRecentPlaceData) {
      allCities.push({
        name: mostRecentPlaceData.Place,
        value: parseFloat((mostRecentPlaceData.population_growth ?? 0).toFixed(2)),
        isSelected: true,
      });
    }

    return allCities.sort((a, b) => a.value - b.value);
  }, [placeData, peerData, peers, placeFips]);

  const calculateRank = () => {
    if (!place) return null;
    const index = chartData.findIndex(item => item.isSelected);
    if (index === -1) return null;
    const rank = chartData.length - index;
    return rank > 0 ? `${rank}${getRankSuffix(rank)}` : null;
  };

  const lastYear = placeData.sort((a, b) => Number(b.year) - Number(a.year))[0]?.year;

  if (!place || !placeFips) {
    return (
      <Card className="p-4 border border-[#ca0270] flex items-center justify-center h-full">
        <CardContent className="text-center">
          <p className="text-gray-500">Waiting for Selection...</p>
        </CardContent>
      </Card>
    );
  }
  
  if (loading) {
    return (
      <Card className="p-4 border border-[#ca0270]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Population Statistics for {formatPlaceName(place)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>Loading population data...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="flex flex-col p-4 border border-[#ca0270]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Population Statistics for {formatPlaceName(place)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="flex flex-col p-4 border border-[#ca0270] relative w-full max-w-[600px]">
        <div className="absolute top-4 left-4 bg-[#908ddb] text-white text-sm font-semibold px-3 py-1 rounded-full">
          Population
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute top-4 right-4 bg-[#ca0270] text-white text-sm font-semibold px-3 py-1 rounded-full cursor-help">
              {peersLoading ? 'Calculating...' : `Peer Ranking: ${calculateRank() || 'N/A'}`}
            </div>
          </TooltipTrigger>
          <TooltipContent className="opacity-100 z-50 bg-white text-black" sideOffset={8}>
            <p>Ranked by: Highest Population Growth Rate</p>
          </TooltipContent>
        </Tooltip>
        <CardHeader className="pt-8 pb-0">
          <CardTitle
            className="text-xl lg:text-2xl font-bold"
          >
            Population Growth
          </CardTitle>
          <p className="text-sm text-gray-500 min-h-[4rem] pt-1">Annual population growth rate from {Number(lastYear) - 1} to {lastYear}</p>
        </CardHeader>
        <CardContent className="relative p-0">
          <ComparisonBarChart
            data={chartData}
            yAxisLabel="Population Growth (%)"
            tooltipFormatter={(value: number) => `${value.toFixed(2)}%`}
            labelFontSize={labelFontSize}
          />
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default PopulationCard; 
